import { useCallback, useMemo, useState } from 'react';

const stepOrder = [
  'records',
  'post-room-completion-feedback',
  'social-sharing',
  'badge-sharing',
  'promote-premium',
  'next-room',
] as const;

export type StepName = (typeof stepOrder)[number];
export type StepKey = Exclude<Partial<typeof stepOrder>['length'], 0>;
export type StepConfig = Record<StepName, boolean>;

const getEnabledStepKeys = (config: StepConfig): StepKey[] =>
  stepOrder
    .map((name, index) => [index + 1, config[name] ?? true] as const)
    .filter(([, enabled]) => enabled)
    .map(([index]) => index as StepKey);

const stepMap = (config: StepConfig) =>
  Object.fromEntries(
    stepOrder.map((name, index) => [index + 1, [name, { enabled: config[name] ?? true }] as const]),
  ) as Record<StepKey, readonly [StepName, { readonly enabled: boolean }]>;

export function useStepNavigator(config: StepConfig) {
  const steps = useMemo(() => stepMap(config), [config]);
  const enabledStepKeys = useMemo(() => getEnabledStepKeys(config), [config]);

  const [stepKey, setCurrentStepKey] = useState<StepKey>(1);

  const onNext = useCallback(() => {
    const idx = enabledStepKeys.indexOf(stepKey);
    if (idx >= 0 && idx < enabledStepKeys.length - 1) {
      setCurrentStepKey(enabledStepKeys[idx + 1] as StepKey);
    }
  }, [stepKey, enabledStepKeys]);

  const onPrevious = useCallback(() => {
    const idx = enabledStepKeys.indexOf(stepKey);
    if (idx > 0) {
      setCurrentStepKey(enabledStepKeys[idx - 1] as StepKey);
    }
  }, [stepKey, enabledStepKeys]);

  const onSkipToStep = useCallback(
    (nextKey: StepKey) => {
      if (enabledStepKeys.includes(nextKey)) {
        setCurrentStepKey(nextKey);
      }
    },
    [enabledStepKeys],
  );

  const isFirstStep = stepKey === enabledStepKeys[0];
  const isLastStep = stepKey === enabledStepKeys[enabledStepKeys.length - 1];

  const currentStep = steps[stepKey];

  return {
    stepKey,
    stepName: currentStep[0],
    onNext,
    onPrevious,
    onSkipToStep,
    isFirstStep,
    isLastStep,
    allSteps: steps,
    enabledStepKeys,
  };
}
